import React, { useReducer, useState, useEffect } from 'react'
import {
    Box,
    FormControlLabel,
    Radio,
    Typography,
    Checkbox,
    styled,
} from '@mui/material'
import { Skeleton } from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { useTranslation } from 'react-i18next'
import PeopleSearch from '../PeopleSearch'
import DateTimeFilter from '../DateTimeFilter'
import Button from '../Button'
import SelectionList from './SelectionList'
import { isNilOrEmpty } from 'packages/core'
import { Spinner, Tooltip } from 'packages/eid-ui'
import moment from 'moment'
import {
    useDelegationTypes,
    useCreateDelegation,
    useJustificationOptions,
} from 'hooks'
import { styles, ItemContainer, reducers } from './utils'
import useSubcomponents from 'useSubcomponents'
import {
    mobileScreenWidth,
    ITEM_COMMENT_MAX_LENGTH,
    ITEM_COMMENT_MIN_LENGTH,
} from 'utils'
import { JustificationSelector } from 'packages/eid-ui/JustificationSelector'

const delegateAllTypesControlName =
    'MyIdentity-PermanentDelegations-DelegateAllTypes-Control'
const delegateSelectedTypesControlName =
    'MyIdentity-PermanentDelegations-DelegateSelectedTypes-Control'

const StyledFormControlLabel = styled(FormControlLabel)(({ checked }) => ({
    color: checked ? '#000000' : '#767676',

    '& input:focus-visible + svg': {
        outline: '0.1rem solid',
        outlineOffset: '0.1rem',
    },
}))

const StyledDateFilterContainer = styled(Box)({
    [`@media (max-width:${mobileScreenWidth})`]: {
        width: '100%',
    },
})

const LabelBox = styled(Box)({
    color: '#73767A',
    fontSize: '1.2rem',
    paddingLeft: '0.8rem',
    marginBottom: '-0.4rem',
})

const StyledListContainer = styled(Box)({
    height: '260px',
    overflow: 'visible',
    width: '40%',
    [`@media (max-width:${mobileScreenWidth})`]: {
        width: '100%',
        height: '250px',
    },
})

const StyledButtonsContainer = styled(Box)({
    width: '20%',
    minWidth: '124px',
    height: '100%',

    padding: '0px 10px',

    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',

    [`@media (max-width:${mobileScreenWidth})`]: {
        width: '100%',
        padding: '20px 0px',
        flexDirection: 'row',
        '& >div:first-of-type': {
            marginTop: '0px',
        },
    },
})

const StyledTransferListsContainer = styled(ItemContainer)({
    display: 'flex',
    width: '100%',
    [`@media (min-width:${mobileScreenWidth})`]: {
        height: '290px',
    },
})

const TypeSelectionControl = ({
    type,
    delegationTypes,
    setType,
    setItemsToAdd,
    dispatch,
}) => {
    const { t } = useTranslation()

    const { hasAccessToControl } = useSubcomponents()

    const hasDelegateAllAccess = hasAccessToControl(delegateAllTypesControlName)

    const hasDelegateSelectedAccess = hasAccessToControl(
        delegateSelectedTypesControlName,
    )

    return hasDelegateAllAccess || hasDelegateSelectedAccess ? (
        <ItemContainer display="flex" flexDirection="column" marginLeft="12px">
            {hasDelegateAllAccess && (
                <StyledFormControlLabel
                    data-protectedsubcomponent={delegateAllTypesControlName}
                    checked={type === 'all'}
                    label={t('AllTypes')}
                    labelPlacement="end"
                    control={
                        <Radio
                            icon={<Icon name="Radio" />}
                            sx={{ padding: '0px 12px 0px 0px' }}
                            checkedIcon={<Icon name="RadioFilled" />}
                        />
                    }
                    onChange={() => {
                        setType('all')
                        setItemsToAdd([])
                        dispatch({
                            type: 'ADD_ITEMS',
                            payload: delegationTypes,
                        })
                    }}
                />
            )}

            {hasDelegateSelectedAccess && (
                <StyledFormControlLabel
                    data-protectedsubcomponent={
                        delegateSelectedTypesControlName
                    }
                    checked={type === 'selectTypes'}
                    label={t('SelectTypes')}
                    labelPlacement="end"
                    control={
                        <Radio
                            sx={{ padding: '0px 12px 0px 0px' }}
                            icon={<Icon name="Radio" />}
                            checkedIcon={<Icon name="RadioFilled" />}
                        />
                    }
                    onChange={() => {
                        setType('selectTypes')
                        dispatch({
                            type: 'REMOVE_ALL_ITEMS',
                            payload: [],
                        })
                    }}
                />
            )}
        </ItemContainer>
    ) : (
        <></>
    )
}

const NewDelegationForm = ({ onSave, onCancel }) => {
    const [state, dispatch] = useReducer(reducers, {
        people: [],
        startDate: moment().startOf('day').utc(),
        endDate: null,
        delegateForever: false,
        activeOnlyIfOutOfOffice: false,
        types: [],
    })
    const [justification, setJustification] = useState({
        comment: '',
        isPredefined: false,
        localKeyName: null,
    })
    const { t } = useTranslation()

    const [type, setType] = useState(null)

    const [itemsToAdd, setItemsToAdd] = useState([])
    const [itemsToRemove, setItemsToRemove] = useState([])

    const { data: delegationTypes } = useDelegationTypes()
    const {
        data: justificationOptions = [],
        isLoading: isLoadingJustifications,
    } = useJustificationOptions()

    const {
        mutateAsync: createDelegation,
        isLoading,
        isSuccess,
    } = useCreateDelegation()

    const { hasAccessToControl } = useSubcomponents()

    const hasJustificationAccess = hasAccessToControl(
        'MyIdentity-PermanentDelegations-Justification-Control',
    )

    const hasOutOfOfficeAccess = hasAccessToControl(
        'MyIdentity-PermanentDelegationsOutOfOfficePermanentDelegation-Control',
    )

    const hasDelegateAllAccess = hasAccessToControl(delegateAllTypesControlName)

    const hasDelegateSelectedAccess = hasAccessToControl(
        delegateSelectedTypesControlName,
    )

    useEffect(() => {
        if (delegationTypes) {
            if (!hasDelegateAllAccess && hasDelegateSelectedAccess) {
                setType('selectTypes')
                dispatch({
                    type: 'REMOVE_ALL_ITEMS',
                    payload: [],
                })
            } else {
                setType('all')
                dispatch({
                    type: 'ADD_ITEMS',
                    payload: delegationTypes,
                })
            }
        }
    }, [delegationTypes])

    const handleCreateDelegation = () => {
        const dataToSubmit = {
            startDateUtc: state.startDate,
            endDateUtc: state.endDate,
            people: state.people.map((p) => p.id),
            types: state.types.map((t) => t.id),
        }
        if (hasOutOfOfficeAccess) {
            dataToSubmit.activeOnlyIfOutOfOffice = state.activeOnlyIfOutOfOffice
        }
        if (hasJustificationAccess) {
            if (justification.isPredefined && justification.localKeyName) {
                // Send localKeyName for predefined options (for localization)
                dataToSubmit.justificationComment = justification.localKeyName
            } else if (justification.comment) {
                // Send custom comment for "Other" option
                dataToSubmit.justificationComment = justification.comment
            }
        }
        createDelegation(dataToSubmit)
    }

    useEffect(() => {
        if (isSuccess) onSave()
    }, [isSuccess, onSave])

    const endDateCheck =
        (!state.delegateForever && isNilOrEmpty(state.endDate)) ||
        state.startDate?.isAfter(state.endDate)

    // Validate justification based on min/max configuration from utils
    const isJustificationValid = () => {
        if (!hasJustificationAccess) {
            return true // No validation if user doesn't have access
        }

        // If a predefined option is selected, it's always valid
        if (justification.isPredefined) {
            return true
        }

        // For "Other" option, validate the custom text
        const commentLength = justification.comment?.length || 0

        // Check min length
        if (commentLength < ITEM_COMMENT_MIN_LENGTH) {
            return false
        }

        // Check max length
        if (commentLength > ITEM_COMMENT_MAX_LENGTH) {
            return false
        }

        return true
    }

    const transferListButtons = delegationTypes && (
        <StyledButtonsContainer>
            <Box marginTop="-8px">
                <Button.Add
                    onClick={() => {
                        dispatch({
                            type: 'ADD_ITEMS',
                            payload: itemsToAdd,
                        })
                        setItemsToAdd([])
                    }}
                    disabled={isNilOrEmpty(itemsToAdd)}
                >
                    {t('Add')}
                </Button.Add>

                <Button.AddAll
                    onClick={() => {
                        dispatch({
                            type: 'ADD_ITEMS',
                            payload: delegationTypes,
                        })
                        setItemsToAdd([])
                    }}
                    disabled={delegationTypes.length === state.types.length}
                >
                    {t('AddAll')}
                </Button.AddAll>
            </Box>

            <Box marginBottom="-8px">
                <Button.Remove
                    startIcon
                    onClick={() => {
                        dispatch({
                            type: 'REMOVE_ITEMS',
                            payload: itemsToRemove,
                        })
                        setItemsToRemove([])
                    }}
                    disabled={isNilOrEmpty(itemsToRemove)}
                >
                    {t('Remove')}
                </Button.Remove>

                <Button.RemoveAll
                    startIcon
                    disabled={isNilOrEmpty(state.types)}
                    onClick={() => {
                        dispatch({
                            type: 'REMOVE_ALL_ITEMS',
                            payload: [],
                        })
                        setItemsToRemove([])
                    }}
                >
                    {t('RemoveAll')}
                </Button.RemoveAll>
            </Box>
        </StyledButtonsContainer>
    )

    return (
        <>
            {!delegationTypes && (
                <>
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        height={16}
                        style={styles.skeleton}
                    />
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        height={16}
                        style={styles.skeleton}
                    />
                </>
            )}

            {delegationTypes && (
                <>
                    <TypeSelectionControl
                        type={type}
                        delegationTypes={delegationTypes}
                        setType={setType}
                        setItemsToAdd={setItemsToAdd}
                        dispatch={dispatch}
                    />
                </>
            )}

            {type === 'selectTypes' && (
                <StyledTransferListsContainer>
                    <StyledListContainer>
                        <SelectionList
                            options={delegationTypes}
                            onChange={(items) => setItemsToAdd(items)}
                            selectedItems={itemsToAdd}
                        />
                    </StyledListContainer>

                    {transferListButtons}

                    <StyledListContainer position="relative" overflow="visible">
                        <Typography style={styles.listLabel}>
                            {t('SelectedTypes')}
                        </Typography>

                        <SelectionList
                            options={state.types}
                            onChange={(items) => setItemsToRemove(items)}
                            selectedItems={itemsToRemove}
                            variant="secondary"
                        />
                    </StyledListContainer>
                </StyledTransferListsContainer>
            )}

            <ItemContainer>
                <PeopleSearch
                    url={'/api/v1/Delegation/approvers'}
                    endAdornmentComponent={() => <></>}
                    label={t('PersonName')}
                    placeholder={t('SelectPerson')}
                    onChange={(_, value) => {
                        dispatch({
                            type: 'SET_PEOPLE',
                            payload: value,
                        })
                    }}
                    rootStyleProps={styles.peopleSearch}
                    value={state.people}
                    multiple
                />
            </ItemContainer>

            <ItemContainer display="flex" marginLeft="12px">
                <StyledFormControlLabel
                    sx={{ marginRight: '30px' }}
                    control={
                        <Checkbox
                            checked={state.delegateForever}
                            icon={<Icon name="CheckBox" />}
                            checkedIcon={<Icon name="CheckedBox" />}
                            onChange={(e) =>
                                dispatch({
                                    type: 'SET_DELEGATE_FOREVER',
                                    payload: e.target.checked,
                                })
                            }
                        />
                    }
                    checked={state.delegateForever}
                    label={t('DelegateForever')}
                />
                {hasOutOfOfficeAccess && (
                    <StyledFormControlLabel
                        data-protectedsubcomponent={
                            'MyIdentity-PermanentDelegationsOutOfOfficePermanentDelegation-Control'
                        }
                        control={
                            <Checkbox
                                checked={state.activeOnlyIfOutOfOffice}
                                icon={<Icon name="CheckBox" />}
                                checkedIcon={<Icon name="CheckedBox" />}
                                onChange={(e) =>
                                    dispatch({
                                        type: 'SET_OUT_OF_OFFICE',
                                        payload: e.target.checked,
                                    })
                                }
                            />
                        }
                        checked={state.activeOnlyIfOutOfOffice}
                        label={t('OnlyWhenOutOfOffice')}
                    />
                )}
            </ItemContainer>

            <ItemContainer display="flex" marginLeft="-8px">
                <StyledDateFilterContainer padding="0px 8px">
                    <DateTimeFilter
                        disablePast={true}
                        margin="normal"
                        label={t('StartDate')}
                        placeholder={t('SelectDate')}
                        onChange={(date) => {
                            let dateToSet
                            if (!isNilOrEmpty(date)) {
                                dateToSet = moment(date)
                                    .clone()
                                    .startOf('day')
                                    .utc()
                            }
                            dispatch({
                                type: 'SET_START_DATE',
                                payload: dateToSet,
                            })
                        }}
                        value={
                            isNilOrEmpty(state.startDate)
                                ? null
                                : state.startDate.clone().local()
                        }
                    />
                </StyledDateFilterContainer>

                {!state.delegateForever && (
                    <StyledDateFilterContainer padding="0px 8px ">
                        <DateTimeFilter
                            minDate={
                                !isNilOrEmpty(state.startDate) &&
                                state.startDate.clone().local()
                            }
                            disabled={state.delegateForever}
                            label={t('EndDate')}
                            placeholder={t('SelectDate')}
                            onChange={(date) => {
                                let dateToSet
                                if (!isNilOrEmpty(date)) {
                                    dateToSet = moment(date)
                                        .clone()
                                        .endOf('day')
                                        .subtract(1, 'minutes')
                                        .utc()
                                }
                                dispatch({
                                    type: 'SET_END_DATE',
                                    payload: dateToSet,
                                })
                            }}
                            value={
                                isNilOrEmpty(state.endDate)
                                    ? null
                                    : state.endDate.clone().local()
                            }
                        />
                    </StyledDateFilterContainer>
                )}
            </ItemContainer>

            {hasJustificationAccess && (
                <ItemContainer
                    data-protectedsubcomponent={
                        'MyIdentity-PermanentDelegations-Justification-Control'
                    }
                >
                    {isLoadingJustifications ? (
                        <>
                            <Spinner size={0.8} />
                        </>
                    ) : (
                        <>
                            <LabelBox>{t('Justification')}</LabelBox>
                            <JustificationSelector
                                options={justificationOptions}
                                value={
                                    justification.isPredefined
                                        ? justification.localKeyName
                                        : justification.comment
                                }
                                onChange={(comment, isPredefined) => {
                                    // For predefined: comment is the localKeyName
                                    // For custom: comment is the actual text
                                    setJustification({
                                        comment: isPredefined ? '' : comment, // Store custom text
                                        isPredefined: isPredefined || false,
                                        localKeyName: isPredefined
                                            ? comment
                                            : null, // Store localKeyName
                                    })
                                }}
                                multiline={true}
                                maxCharacters={ITEM_COMMENT_MAX_LENGTH}
                                minCharacters={ITEM_COMMENT_MIN_LENGTH}
                                showCharacterCount={true}
                                isLoading={isLoadingJustifications}
                            />
                        </>
                    )}
                </ItemContainer>
            )}
            <ItemContainer
                display="flex"
                marginLeft="-8px"
                alignItems="center"
                flexDirection="row !important"
                flexWrap="wrap"
            >
                <Tooltip
                    title={
                        state.types.length === 0 ||
                        isNilOrEmpty(state.startDate) ||
                        isNilOrEmpty(state.people) ||
                        endDateCheck
                            ? t('RequiredFieldsMissing')
                            : ''
                    }
                >
                    <Box>
                        <Button.Rounded
                            loading={isLoading}
                            styles={{ minWidth: '144px !important' }}
                            disabled={
                                state.types.length === 0 ||
                                isNilOrEmpty(state.startDate) ||
                                isNilOrEmpty(state.people) ||
                                endDateCheck ||
                                isLoading ||
                                !isJustificationValid()
                            }
                            onClick={() => {
                                handleCreateDelegation()
                            }}
                        >
                            {t('CreateDelegation')}
                        </Button.Rounded>
                    </Box>
                </Tooltip>

                <Button.Rounded onClick={onCancel} styles={styles.cancelButton}>
                    {t('Cancel')}
                </Button.Rounded>
            </ItemContainer>
        </>
    )
}

export default NewDelegationForm
